import React from "react";
import { useSelector } from "react-redux";

import {
  selectCostKpis,
  selectCostBreakdownReport,
  selectMonthlyTrends,
  selectCostReportLoading,
} from "../../../../slices/report/selectors";

import CostKpiCards from "../components/CostKpiCards";
import DonutChart from "../components/DonutChart";
import TrendsChart from "../components/TrendsChart";
import CashFlowSection from "../components/CashFlowSection";
import CostHierarchyTable from "../components/CostHierarchyTable";
import CostTypeStackedChart from "../components/CostTypeStackedChart";
import VendorList from "../components/VendorList";

const DONUT_COLORS = [
  "#002045",
  "#505f76",
  "#7c3aed",
  "#0891b2",
  "#059669",
  "#d97706",
];

const BudgetView = ({ phaseId }) => {
  const kpis = useSelector(selectCostKpis);
  const costBreakdown = useSelector(selectCostBreakdownReport);
  const monthlyTrends = useSelector(selectMonthlyTrends);
  const reportLoading = useSelector(selectCostReportLoading);

  const totalBudget = kpis?.total_budget ?? null;
  const totalSpent = kpis?.total_spent ?? null;
  const committed = kpis?.total_committed ?? 0;
  const unexpectedBudget = kpis?.unexpected_budget ?? 0;
  const unexpectedSpent = kpis?.unexpected_spent ?? 0;
  const unexpectedRemaining = kpis?.unexpected_remaining ?? 0;
  const unexpectedAbsorbed = kpis?.unexpected_overrun_absorbed ?? 0;

  const donutTotal = costBreakdown.reduce(
    (sum, ct) => sum + (ct.budget ?? 0),
    0,
  );
  const donutSegments =
    donutTotal > 0
      ? costBreakdown.map((ct, i) => ({
          label: ct.name,
          pct: ((ct.budget ?? 0) / donutTotal) * 100,
          color: DONUT_COLORS[i % DONUT_COLORS.length],
        }))
      : undefined;

  const tableGroups = costBreakdown.map((ct) => ({
    id: ct.id,
    name: ct.name,
    budget: ct.budget ?? 0,
    spent: ct.spent ?? 0,
    committed: ct.committed ?? 0,
    categories: (ct.categories || []).map((cat) => ({
      name: cat.name,
      budget: cat.budget ?? 0,
      spent: cat.spent ?? 0,
      committed: cat.committed ?? 0,
    })),
  }));

  return (
    <>
      <CostKpiCards
        totalBudget={totalBudget}
        totalSpent={totalSpent}
        committed={committed}
        remaining={totalBudget - totalSpent - committed}
        unexpectedBudget={unexpectedBudget}
        unexpectedSpent={unexpectedSpent}
        unexpectedRemaining={unexpectedRemaining}
        unexpectedAbsorbed={unexpectedAbsorbed}
      />

      <CostTypeStackedChart months={monthlyTrends} loading={reportLoading} />

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 2fr",
          gap: 24,
          marginBottom: 32,
        }}
      >
        <DonutChart
          segments={donutSegments}
          centerLabel={
            totalBudget == null
              ? "—"
              : totalBudget >= 1_000_000
                ? `${(totalBudget / 1_000_000).toFixed(1)}M`
                : totalBudget.toLocaleString()
          }
          centerSub={phaseId ? "Phase Budget" : "Total Budget"}
        />
        <TrendsChart months={monthlyTrends} />
      </div>

      <div style={{ marginBottom: 32 }}>
        <CashFlowSection
          paid={totalSpent}
          committed={committed}
          total={totalBudget}
        />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "280px 1fr", gap: 24, alignItems: "start", paddingBottom: 32 }}>
        <VendorList phaseId={phaseId} />
        <CostHierarchyTable
          groups={tableGroups.length > 0 ? tableGroups : undefined}
        />
      </div>
    </>
  );
};

export default BudgetView;
